// DSST(기호 맞추기) 신한 SOL 버전 — 게임 설정 및 로직
// 기존 dsst 와 동일한 규칙을 사용하되,
//  - 심볼 세트를 제공된 4종 세트로 교체하고
//  - 연습 단계/스테이지 구분 없이 "총 45초 1스테이지"로 진행하며
//  - 5문제 이상 연속 정답이면 기호 그룹이 교체된다.

export const SYMBOL_COUNT = 6; // 사용 기호 개수
export const DSST_LEVEL_COUNT = 1; // 스테이지(레벨) 수 — 단일 스테이지

// 총 제한시간(초)
export const DSST_TOTAL_DURATION = 45;
export const DSST_LEVEL_DURATIONS = [DSST_TOTAL_DURATION];

// 한 스테이지에서 사용 가능한 심볼 세트 (게임 시작 시 무작위 선택)
// 0 = 세트 A (도형), 1 = 세트 B (도형)
// 2 = 세트 C (사각 회전), 3 = 세트 D (원 회전)
export const ALL_SYMBOL_SETS = [0, 1, 2, 3];
export const LEVEL_SYMBOL_SETS: number[][] = [ALL_SYMBOL_SETS];

// 연속 정답 몇 문제마다 기호 그룹을 교체할지
export const COMBO_SET_CHANGE = 5;

// 시작 시 사용할 심볼 세트를 무작위로 하나 고른다
export function getRandomSymbolSetForLevel(levelIndex: number): number {
  const sets = LEVEL_SYMBOL_SETS[levelIndex] ?? LEVEL_SYMBOL_SETS[0];
  return sets[Math.floor(Math.random() * sets.length)];
}

// 현재 사용 중인 세트와 다른 세트를 무작위로 고른다 (그룹 교체용)
export function getDifferentSymbolSet(current: number): number {
  const pool = ALL_SYMBOL_SETS.filter((s) => s !== current);
  return pool[Math.floor(Math.random() * pool.length)];
}

// 제한시간(초)
export function getLevelDuration(levelIndex: number): number {
  return DSST_LEVEL_DURATIONS[levelIndex] ?? DSST_LEVEL_DURATIONS[0];
}

export const CORRECT_FEEDBACK_MS = 180; // 정답 피드백 시간(ms)
export const WRONG_FEEDBACK_MS = 300; // 오답 피드백 시간(ms) — 정답보다 약간 길게 인지

// 점수 산식 상수 (향후 조정 가능하도록 별도 분리)
export const DSST_MAX_EFFECTIVE_CORRECT = 27;
export const DSST_MIN_SCORE = 20; // 최저점
export const DSST_MAX_SCORE = 100; // 최고점
export const DSST_WRONG_PENALTY = 0.5; // 오답 1개당 유효정답 차감

// Combo(연속 정답) 마일스톤: 3 / 5 / 10 / 15 / 20 ... (5 이후 5회 단위)에서 강화 피드백
export function isComboMilestone(combo: number): boolean {
  if (combo === 3) return true;
  if (combo === 5) return true;
  return combo >= 10 && combo % 5 === 0;
}

// 심볼 세트 명칭 (접근성/디버깅용)
export const SYMBOL_SET_NAMES = [
  '세트 A · 도형',
  '세트 B · 도형',
  '세트 C · 사각 회전',
  '세트 D · 원 회전',
];

// 정답/오답 피드백 상태
export type Feedback = 'correct' | 'wrong' | null;

// 문항별 기록 (원천 데이터)
export interface QuestionRecord {
  level: number; // 진행 레벨 (단일 스테이지이므로 항상 0)
  symbolId: number; // 출제 기호
  answer: number; // 사용자 선택 답안
  correctAnswer: number; // 정답 숫자
  isCorrect: boolean; // 정답 여부
  reactionTimeMs: number; // 반응시간(ms)
}

// 본게임 최종 수집 데이터
export interface DsstGameData {
  gameType: string;
  performedAt: string;
  totalQuestions: number;
  correctCount: number;
  wrongCount: number;
  accuracy: number;
  questions: QuestionRecord[];
  averageReactionTimeMs: number;
  symbolAccuracy: Record<number, { correct: number; total: number; rate: number }>;
  symbolAvgReactionMs: Record<number, number>;
  score: number;
  isAborted: boolean;
}

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// 기호 ↔ 숫자 대응표 생성 (숫자 1~6을 무작위 배정)
export function generateSymbolMap(): number[] {
  return shuffle([1, 2, 3, 4, 5, 6]);
}

// 숫자 패널 표시 순서 (1~6을 무작위 섞음) — 본게임에서 문제마다 랜덤 위치 변경용
export function shuffleNumbers(): number[] {
  return shuffle([1, 2, 3, 4, 5, 6]);
}

// 안내 화면용 기본 대응표 (기호 1번=1 ... 6번=6)
export const DEFAULT_SYMBOL_MAP: number[] = [1, 2, 3, 4, 5, 6];

function clamp(v: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, v));
}

function roundToNearest5(v: number): number {
  return Math.round(v / 5) * 5;
}

// 점수 산출: 유효정답 수(정답 - 오답×0.5) 기반 100점 환산
export function calculateScore(correct: number, wrong: number): number {
  const effectiveCorrect = correct - wrong * DSST_WRONG_PENALTY;
  const raw =
    DSST_MIN_SCORE +
    (effectiveCorrect / DSST_MAX_EFFECTIVE_CORRECT) * (DSST_MAX_SCORE - DSST_MIN_SCORE);
  const rounded = roundToNearest5(raw);
  return clamp(rounded, DSST_MIN_SCORE, DSST_MAX_SCORE);
}

// 균형 출제: 동일 기호 최대 2회 연속 제한 + 전체 빈도 균형 유지
export function pickNextSymbol(history: number[]): number {
  const counts = new Array(SYMBOL_COUNT).fill(0);
  history.forEach((s) => {
    counts[s] += 1;
  });

  // 직전 2개가 같은 기호면 3연속을 막기 위해 제외
  let blocked = -1;
  if (history.length >= 2) {
    const last = history.slice(-2);
    if (last[0] === last[1]) blocked = last[0];
  }

  let minCount = Infinity;
  let candidates: number[] = [];
  for (let i = 0; i < SYMBOL_COUNT; i++) {
    if (i === blocked) continue;
    if (counts[i] < minCount) {
      minCount = counts[i];
      candidates = [i];
    } else if (counts[i] === minCount) {
      candidates.push(i);
    }
  }
  return candidates[Math.floor(Math.random() * candidates.length)];
}